import {
  GEOLOCATION_AVAILABILITY_TYPES,
  type GeolocationApiModel,
  type GeolocationAvailabilityType,
  type LeafletGeolocationApiModel
} from '@/types/GeolocationModel'

export const DEFAULT_GEOCODING_RADIUS = 10_000
export const DEFAULT_GEOLOCATION_AVAILABILITY_TYPE: GeolocationAvailabilityType = 'AVAILABLE'
const DECIMAL_NUMBER_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/

export type GeolocationParsingOptions = {
  includeAvailabilityType?: boolean
}

export type ParsedGeolocationLine =
  | {
      type: 'coordinates'
      original: string
      value: GeolocationApiModel
    }
  | {
      type: 'address'
      original: string
      value: string
    }

function parseFiniteNumber(value: string): number | null {
  const trimmed = value.trim()
  if (!DECIMAL_NUMBER_PATTERN.test(trimmed)) return null

  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

function parseAvailabilityType(value: string | undefined): GeolocationAvailabilityType | null {
  if (value === undefined) return DEFAULT_GEOLOCATION_AVAILABILITY_TYPE

  const trimmed = value.trim()
  return GEOLOCATION_AVAILABILITY_TYPES.find(type => type === trimmed) ?? null
}

export function parseCoordinateLine(
  line: string,
  { includeAvailabilityType = false }: GeolocationParsingOptions = {}
): LeafletGeolocationApiModel | null {
  const parts = line.split(',')
  if (parts.length < 3) return null

  const lat = parseFiniteNumber(parts[0])
  const lng = parseFiniteNumber(parts[1])
  const radius = parseFiniteNumber(parts[2])
  const availabilityType = parseAvailabilityType(includeAvailabilityType ? parts[3] : undefined)
  const name = parts
    .slice(includeAvailabilityType ? 4 : 3)
    .join(',')
    .trim()

  if (lat === null || lng === null || radius === null || availabilityType === null) return null
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null
  if (!Number.isInteger(radius) || radius <= 0) return null

  return { lat, lng, radius, availabilityType, ...(name ? { name } : {}) }
}

export function parseGeolocationLines(
  geolocations: string,
  options: GeolocationParsingOptions = {}
): ParsedGeolocationLine[] {
  if (!geolocations) return []

  return geolocations
    .split(/\n|(?<=\d)\s+(?=[+-]?(?:\d+(?:\.\d*)?|\.\d+)\s*,)/)
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const coordinates = parseCoordinateLine(line, options)

      if (coordinates) {
        return {
          type: 'coordinates',
          original: line,
          value: coordinates
        }
      }

      return {
        type: 'address',
        original: line,
        value: line
      }
    })
}

export function extractCoordinates(
  geolocations: string,
  options: GeolocationParsingOptions = {}
): GeolocationApiModel[] {
  return parseGeolocationLines(geolocations, options)
    .filter(line => line.type === 'coordinates')
    .map(line => line.value)
}

export function extractAddresses(geolocations: string, options: GeolocationParsingOptions = {}): string[] {
  return parseGeolocationLines(geolocations, options)
    .filter(line => line.type === 'address')
    .map(line => line.value)
}

export function isValidGeolocationsValue(geolocations: string, options: GeolocationParsingOptions = {}): boolean {
  if (!geolocations.trim()) return true

  const lines = parseGeolocationLines(geolocations, options)
  return lines.length > 0 && lines.every(line => line.type === 'coordinates')
}

export function formatGeolocation(
  { lat, lng, radius, availabilityType, name }: GeolocationApiModel,
  includeAvailabilityType = false
): string {
  const baseValue = `${lat},${lng},${radius}`

  if (!includeAvailabilityType) return name ? `${baseValue},${name}` : baseValue

  const combined = `${baseValue},${availabilityType ?? DEFAULT_GEOLOCATION_AVAILABILITY_TYPE}`
  return name ? `${combined},${name}` : combined
}
